import React, { useCallback, useContext, useRef, useEffect, useState, } from 'react';
import { useAdsData } from '@24i/player-utils-react';
import { PlayerUIContext, PlayerUIContextProvider, showUI } from '@24i/player-ui';
import { getTelegramWebApp } from '@24i/nxg-sdk-higgs/src/utils';
import { Container } from './styled';
const PERCENT_OFFSET_FROM_BORDER_TO_SEEK = 20;
const FULL_PERCENT = 100;
const SEEK_AMOUNT = 10000;
const VOLUME_STEP = 0.05;
function isMobileWeb() {
    if (typeof navigator === 'undefined')
        return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}
export function handlePlayPause(playerInstance, overlayVisible, dispatch, showOnlyOverlay = false) {
    if (!playerInstance)
        return;
    if (showOnlyOverlay) {
        dispatch(showUI());
        return;
    }
    if (overlayVisible) {
        if (playerInstance.paused)
            playerInstance.play();
        else
            playerInstance.pause();
    }
    dispatch(showUI());
}
export function handleFullscreen(playerInstance, TelegramWebApp, webViewFullscreen, setWebViewFullscreen) {
    if (!playerInstance)
        return;
    if (TelegramWebApp) {
        if (webViewFullscreen) {
            TelegramWebApp.exitFullscreen();
            setWebViewFullscreen(false);
        }
        else {
            TelegramWebApp.requestFullscreen();
            setWebViewFullscreen(true);
        }
    }
    else {
        try {
            playerInstance.fullscreen = !playerInstance.fullscreen;
        }
        catch (error) {
            console.warn('Fullscreen toggle failed:', error);
        }
    }
}
export function handleSeek(playerInstance, amount) {
    if (!playerInstance)
        return;
    if (amount < 0)
        playerInstance.backward(Math.abs(amount));
    else
        playerInstance.forward(amount);
}
export function handleMute(playerInstance) {
    if (!playerInstance || typeof playerInstance.muted !== 'boolean')
        return;
    playerInstance.muted = !playerInstance.muted;
}
export const WithoutContextPlayerUIContainer = ({ playerInstance, blockingReason, children, className, style, }) => {
    const { state: { isDragSeeking, uiDisplayed }, dispatch, } = useContext(PlayerUIContext);
    const containerRef = useRef(null);
    const { isPlayingClientAd } = useAdsData(playerInstance);
    const [webViewFullscreen, setWebViewFullscreen] = useState(false);
    useEffect(() => {
        const handleVolumeAdjustment = (increase) => {
            if (playerInstance && typeof playerInstance.volume === 'number') {
                if (increase) {
                    playerInstance.volume = Math.min(1, playerInstance.volume + VOLUME_STEP);
                }
                else {
                    playerInstance.volume = Math.max(0, playerInstance.volume - VOLUME_STEP);
                }
            }
        };
        const keyHandlers = {
            Space: () => handlePlayPause(playerInstance, uiDisplayed, dispatch, false),
            Enter: () => handlePlayPause(playerInstance, uiDisplayed, dispatch, false),
            ArrowLeft: () => handleSeek(playerInstance, -SEEK_AMOUNT),
            ArrowRight: () => handleSeek(playerInstance, SEEK_AMOUNT),
            ArrowUp: () => handleVolumeAdjustment(true),
            ArrowDown: () => handleVolumeAdjustment(false),
            KeyF: () => handleFullscreen(playerInstance, getTelegramWebApp(), webViewFullscreen, setWebViewFullscreen),
            Keyf: () => handleFullscreen(playerInstance, getTelegramWebApp(), webViewFullscreen, setWebViewFullscreen),
            KeyM: () => handleMute(playerInstance),
            Keym: () => handleMute(playerInstance),
        };
        const handler = (event) => {
            const tag = event.target && event.target.tagName;
            if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || event.repeat) {
                return;
            }
            const keyHandler = keyHandlers[event.code];
            if (keyHandler) {
                keyHandler();
                event.preventDefault();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [playerInstance, uiDisplayed, dispatch, webViewFullscreen]);
    const handleAdLogic = useCallback(() => {
        if (isPlayingClientAd && (playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.paused) && playerInstance.currentTime === 0) {
            playerInstance.play();
            return true;
        }
        return false;
    }, [isPlayingClientAd, playerInstance]);
    const handlePlatformPointer = useCallback(() => {
        if (isMobileWeb()) {
            handlePlayPause(playerInstance, uiDisplayed, dispatch, true);
            return;
        }
        if (!uiDisplayed) {
            dispatch(showUI());
        }
        else {
            handlePlayPause(playerInstance, uiDisplayed, dispatch, false);
        }
    }, [playerInstance, uiDisplayed, dispatch]);
    const onPointerHandler = useCallback(() => {
        if (isDragSeeking)
            return;
        if (blockingReason)
            return;
        if (handleAdLogic())
            return;
        handlePlatformPointer();
    }, [isDragSeeking, blockingReason, handleAdLogic, handlePlatformPointer]);
    const handleMobileDoubleClick = useCallback((event) => {
        const x = event.nativeEvent.offsetX;
        const width = event.target.offsetWidth;
        const percent = (x / width) * 100;
        if (percent < PERCENT_OFFSET_FROM_BORDER_TO_SEEK) {
            handleSeek(playerInstance, -SEEK_AMOUNT);
        }
        else if (percent > FULL_PERCENT - PERCENT_OFFSET_FROM_BORDER_TO_SEEK) {
            handleSeek(playerInstance, SEEK_AMOUNT);
        }
        return true;
    }, [playerInstance]);
    const onDoubleClickHandler = useCallback((event) => {
        if (event.target !== containerRef.current)
            return;
        if (isMobileWeb()) {
            handleMobileDoubleClick(event);
        }
        else {
            handleFullscreen(playerInstance, getTelegramWebApp(), webViewFullscreen, setWebViewFullscreen);
        }
        event.stopPropagation();
        event.preventDefault();
    }, [playerInstance, webViewFullscreen, handleMobileDoubleClick]);
    return (React.createElement(Container, { ref: containerRef, className: `PlayerUIContainer ${className}`, "data-test": "PlayerUIContainer-Container", "data-testid": "PlayerUIContainer-Container", id: "PlayerUIContainer-Container", onMouseDown: onPointerHandler, onTouchStart: onPointerHandler, onDoubleClick: onDoubleClickHandler, style }, children));
};
WithoutContextPlayerUIContainer.displayName = 'WithoutContextPlayerUIContainer';
export const PlayerUIContainer = (props) => (React.createElement(PlayerUIContextProvider, null,
    React.createElement(WithoutContextPlayerUIContainer, Object.assign({}, props))));
PlayerUIContainer.displayName = 'PlayerUIContainer';
PlayerUIContainer.defaultProps = {
    className: '',
    style: null,
};
//# sourceMappingURL=index.js.map